import type * as pf from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { ut_addUrlParams, ut_copyParams } from '@base/utils/utils'
import { useAppIndexStore } from '@base/stores/indexStore'
import { ENotificationType } from '@base/utils/constants'
import { customerModifyQuery } from '@customerQuery'

export function usePfinderAPI() {
  const runtimeConfig = useRuntimeConfig()
  // Read here, in the factory: inside fetchData, after an await, the Nuxt instance may be gone on the server.
  const botParam: string = useAppConfig().botEmbParam || ''
  const indexStore = useAppIndexStore()

  // SSR: pass the incoming end-user IP on to pfinder, so its per-IP accounting sees the real user
  // instead of the one Nuxt cluster IP. In the browser useRequestHeaders gives {} — there $fetch
  // carries the real IP by itself.
  //
  // x-pfinder-caller-ip / x-pfinder-caller-ua: the same caller under own names, set by the CMS
  // embedding (deploy/cms/index.php) because a reverse proxy may overwrite the standard headers but
  // passes own names through. The caller's agent travels only under its own name: our own
  // User-Agent stays, so the pfinder log still tells the SSR gateway from a direct browser call.
  const ssrHeaders: Record<string, string> = import.meta.server
    ? useRequestHeaders(['x-forwarded-for', 'x-real-ip', 'x-pfinder-caller-ip', 'x-pfinder-caller-ua'])
    : {}
  const fwdHeaders: Record<string, string> = {}
  for (const name of ['x-forwarded-for', 'x-real-ip', 'x-pfinder-caller-ip', 'x-pfinder-caller-ua']) {
    if (ssrHeaders[name]) { fwdHeaders[name] = ssrHeaders[name] }
  }

  async function fetchData<T extends pf.PfCmdType>(cmd: T, args: pf.PfCmd<T>['arg'], data: tp.IFetchData): Promise<pf.ResponseData & { result: pf.PfCmd<T>['res'] } | undefined> {
    const paramsQuery: tp.IParamQueryServer = data.paramsQuery || {}
    const paramData: tp.IParamData = data.paramData || indexStore.state.paramData
    // SSR only: a caller the CMS marked as a bot travels as x-client-bot, so the server can turn bots
    // away before people when it is overloaded (see botEmbParam in the app.config). Never in the
    // browser: an own header on a cross-origin call would cost a CORS preflight per request.
    const isBot: boolean = import.meta.server && !!botParam && (paramData.embeddedInfo as Record<string, string | undefined> | undefined)?.[botParam] === 'true'
    const headers: Record<string, string> = isBot ? { ...fwdHeaders, 'x-client-bot': '1' } : fwdHeaders

    // Set the actual country and language if nothing is set
    if (!paramsQuery.rcco) { paramsQuery.rcco = paramData.rcco }
    if (!paramsQuery.lcda) { paramsQuery.lcda = paramData.lcda }

    // standard URLServer params, only copy if not call without url params
    if (!data.noUrlParams) {
      ut_copyParams(paramData.queryUrlServer, paramsQuery, paramData.queryUrlServer, false, true)
    // Only global params
    }
    else {
      if (paramData.queryUrlServer.stage) { paramsQuery.stage = paramData.queryUrlServer.stage }
      if (paramData.queryUrlServer.dops) { paramsQuery.dops = paramsQuery.dops || paramData.queryUrlServer.dops }
      if (paramData.queryUrlServer.rcst) { paramsQuery.rcst = paramsQuery.rcst || paramData.queryUrlServer.rcst }
      if (paramData.queryUrlServer.rcre1) { paramsQuery.rcre1 = paramsQuery.rcre1 || paramData.queryUrlServer.rcre1 }
      if (paramData.queryUrlServer.rcre2) { paramsQuery.rcre2 = paramsQuery.rcre2 || paramData.queryUrlServer.rcre2 }
      if (paramData.queryUrlServer.date) { paramsQuery.date = paramsQuery.date || paramData.queryUrlServer.date }
    }

    // Add customer specific modified query params
    ut_copyParams(indexStore.state.modifiedQueryParams, paramsQuery, indexStore.state.modifiedQueryParams, false, true)

    // Customer-specific, per-call query modification (data/query based, overridable per client)
    customerModifyQuery(paramsQuery, paramData)

    const url: string = [runtimeConfig.public.dataHost || '', runtimeConfig.public.pfinderService || '', <T>(cmd)].join('/')
    const method = data.usePost ? 'post' : 'get'
    const params: any = { ...args, ...paramsQuery }

    // Delete customer specific modified query params always only for 1 call
    indexStore.setModifiedQueryParams({})

    try {
      let res: pf.ResponseData & { result: pf.PfCmd<T>['res'] }
      if (method === 'get') {
        res = await $fetch<pf.ResponseData & { result: pf.PfCmd<T>['res'] }>(ut_addUrlParams(url, params), { method, headers })
      }
      else {
        res = await $fetch<pf.ResponseData & { result: pf.PfCmd<T>['res'] }>(url, { method, headers, body: params }) as pf.ResponseData & { result: pf.PfCmd<T>['res'] }
      }
      return res
    }
    catch (err) {
      const e = err as Error & { data?: { message?: string } }
      console.error(e)
      // Prefer the server's own message over the transport one. $fetch puts the parsed response
      // body on err.data, while e.message is only '[GET] "<url>": 500 Internal Server Error' —
      // which tells a user nothing. The server sends the real cause, e.g. the original spaix text
      // '"Invalid API key: ..." [Error:1, Code:1]', so the user can pass it on to Wilo and it can
      // actually be investigated. Same wording the direct spaix call already shows in PumpCurve.
      const notification: tp.INotification = {
        title: 'Error fetching data',
        message: e.data?.message || e.message,
        id: JSON.stringify(url),
        type: ENotificationType.nt_warning,
      }
      indexStore.setNotification(notification)
      return undefined
    }
  }

  return {
    fetchData,
  }
}
